import clsx from 'clsx';
import {Link} from 'react-router-dom';
import {Trans} from '../../i18n/trans';
import {useThemeSelector} from '../themes/theme-selector-context';
import {useSettings} from '../../core/settings/use-settings';
import {Logo} from '@common/ui/navigation/navbar/logo';
import './footer.css';

interface Props {
  className?: string;
  padding?: string;
}

const FOOTER_COLS = [
  {
    title: 'Product',
    links: [
      {label: 'Features', href: '/#features'},
      {label: 'Pricing', href: '/pricing'},
      {label: 'Templates', href: '/templates'},
      {label: 'Changelog', href: '/changelog'},
      {label: 'Roadmap', href: '/roadmap'},
    ],
  },
  {
    title: 'Resources',
    links: [
      {label: 'Documentation', href: '/docs'},
      {label: 'Guides', href: '/guides'},
      {label: 'API Reference', href: '/api'},
      {label: 'Status', href: '/status'},
      {label: 'Help Center', href: '/help'},
    ],
  },
  {
    title: 'Company',
    links: [
      {label: 'About us', href: '/about'},
      {label: 'Blog', href: '/blog'},
      {label: 'Careers', href: '/careers'},
      {label: 'Partnerships', href: '/partnerships'},
      {label: 'Contact', href: '/contact'},
    ],
  },
  {
    title: 'Legal',
    links: [
      {label: 'Terms of service', href: '/terms'},
      {label: 'Privacy policy', href: '/privacy'},
      {label: 'Cookie policy', href: '/cookies'},
      {label: 'Security', href: '/security'},
    ],
  },
];

export function Footer({className}: Props) {
  const year = new Date().getFullYear();
  const {branding} = useSettings();
  const {selectedTheme, selectTheme} = useThemeSelector();
  const {themes} = useSettings();
  const isDark = selectedTheme?.is_dark;

  return (
    <footer className={clsx('rp-footer', className)}>
      <div className="rp-footer__inner">
        {/* Left brand column */}
        <div className="rp-footer__brand">
          <Link to="/" className="rp-footer__logo-link" aria-label="Home">
            <Logo />
          </Link>
          <p className="rp-footer__tagline">
            Build and launch beautiful websites in minutes with AI.
          </p>
          <div className="rp-footer__socials">
            {/* X / Twitter */}
            <a href="#" className="rp-footer__social-btn" aria-label="X / Twitter">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.742l7.746-8.862L2.042 2.25H8.91l4.265 5.638L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z"/>
              </svg>
            </a>
            {/* LinkedIn */}
            <a href="#" className="rp-footer__social-btn" aria-label="LinkedIn">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </a>
            {/* GitHub */}
            <a href="#" className="rp-footer__social-btn" aria-label="GitHub">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"/>
              </svg>
            </a>
          </div>
          {/* Theme switcher */}
          {selectedTheme && themes?.user_change && (
            <button
              className="rp-footer__theme-btn"
              onClick={() => selectTheme(isDark ? 'light' : 'dark')}
            >
              {isDark ? (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="5"/><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>
                  <Trans message="Light mode" />
                </>
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/></svg>
                  <Trans message="Dark mode" />
                </>
              )}
            </button>
          )}
        </div>

        {/* Link columns */}
        <div className="rp-footer__cols">
          {FOOTER_COLS.map(col => (
            <div className="rp-footer__col" key={col.title}>
              <h4 className="rp-footer__col-title">{col.title}</h4>
              <ul className="rp-footer__col-list">
                {col.links.map(lk => (
                  <li key={lk.href}>
                    <Link to={lk.href} className="rp-footer__col-link">
                      {lk.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom bar */}
      <div className="rp-footer__bottom">
        <div className="rp-footer__bottom-inner">
          <span>
            <Trans
              message="Copyright © :year :name, All Rights Reserved"
              values={{year, name: branding.site_name}}
            />
          </span>
          <div className="rp-footer__bottom-links">
            <Link to="/privacy" className="rp-footer__bottom-link">Privacy</Link>
            <Link to="/terms" className="rp-footer__bottom-link">Terms</Link>
            <Link to="/cookies" className="rp-footer__bottom-link">Cookies</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
